import { useIsAuthenticated } from "@azure/msal-react";
import {
  Table,
  TableCaption,
  Thead,
  Tr,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { useState, useCallback, useEffect, useMemo } from "react";
import { ScenarioModel, getScenarioList } from "../../clients/BackOfficeApi";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { ScenarioListRow } from "./ScenarioListRow";
import { ScenarioHeaderRow } from "./ScenarioHeaderRow";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

export const ScenarioTable = withPagination(
  { settingsKey: "scenarios" },
  ({ situationId }: { situationId?: number }) => {
    const [scenarios, setScenarios] = useState<ScenarioModel[]>([]);
    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const getScenarios = useCallback(
      (refetch = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const abortController = new AbortController();

        const query = refetch
          ? { Offset: 0, Limit: offset + limit, situationId }
          : { Offset: offset, Limit: limit, situationId };

        getScenarioList({
          query,
          signal: abortController.signal,
          throwOnError: true,
        })
          .then(
            ({ data }) => {
              if (abortController.signal.aborted) return;

              const items = data?.items ?? [];

              const loadedItems = items.length;
              setLoadedOffset(query.Offset + loadedItems);
              setScenarios((prev) => {
                prev.splice(query.Offset, loadedItems, ...items);
                return prev.slice(0, query.Offset + loadedItems);
              });
              setTotal(data?.totalCount ?? 0);
            },
            (e) => {
              if (abortController.signal.aborted) return;

              throw e;
            },
          )
          .finally(() => setLoading(false));

        return () =>
          abortController.abort("Component unmounted or re-rendered");
      },
      [isAuthenticated, limit, offset, situationId],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => getScenarios(), [getScenarios]);

    return (
      <Table variant="striped" w="auto">
        <TableCaption placement="top">Scenario&apos;s ({total})</TableCaption>
        <Thead>
          <ScenarioHeaderRow
            ref={(el: HTMLTableRowElement | null) =>
              setTableSpan(el?.childElementCount ?? 1)
            }
            includeSituationDetails={!situationId}
          />
        </Thead>
        <Tbody>
          <TableLoadingRow colSpan={tableSpan} loading={!moreRowsAvailable && loading} />
          {!scenarios.length && !loading && (
            <Tr>
              <Td colSpan={tableSpan}>Geen scenario&apos;s gevonden</Td>
            </Tr>
          )}
          {scenarios.map((scenario) => (
            <ScenarioListRow
              key={scenario.id}
              includeSituationDetails={!situationId}
              onChange={() => getScenarios(true)}
              scenario={scenario}
            />
          ))}
          {moreRowsAvailable && (
            <Tr>
              <Td colSpan={tableSpan}>
                <PaginationForm loading={loading} offset={loadedOffset} />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    );
  },
);
